<!--
  UserQueryView.vue — 客服查询页面
  左侧：搜索框 + 用户列表，右侧：记录内容区域
-->
<template>
  <div class="uq-page">
    <div class="uq-layout">
      <!-- ========== 左侧面板：搜索 + 用户列表 ========== -->
      <div class="uq-left">
        <div class="uq-search-box">
          <a-radio-group
            v-model:value="searchMode"
            size="small"
            button-style="solid"
            style="display: flex; margin-bottom: 10px"
          >
            <a-radio-button
              value="phone"
              style="flex: 1; text-align: center"
            >手机号</a-radio-button>
            <a-radio-button
              value="order"
              style="flex: 1; text-align: center"
              disabled
            >订单号</a-radio-button>
            <a-radio-button
              value="time"
              style="flex: 1; text-align: center"
              disabled
            >支付时间</a-radio-button>
          </a-radio-group>

          <!-- 手机号 -->
          <a-textarea
            v-if="searchMode === 'phone'"
            v-model:value="phone"
            placeholder="手机号，多个用逗号或换行分隔"
            allow-clear
            :auto-size="{ minRows: 3, maxRows: 6 }"
            @press-enter="doSearch"
          />

          <!-- 订单号 -->
          <a-textarea
            v-if="searchMode === 'order'"
            v-model:value="orderNo"
            placeholder="输入订单编号"
            allow-clear
            :auto-size="{ minRows: 3, maxRows: 6 }"
            @press-enter="doSearch"
          />

          <!-- 支付时间范围 -->
          <template v-if="searchMode === 'time'">
            <a-date-picker
              v-model:value="payTimeStart"
              placeholder="开始日期"
              style="width: 100%"
              value-format="YYYY-MM-DD"
            />
            <a-date-picker
              v-model:value="payTimeEnd"
              placeholder="结束日期"
              style="width: 100%; margin-top: 6px"
              value-format="YYYY-MM-DD"
            />
          </template>

          <a-button
            type="primary"
            :loading="loading"
            style="margin-top: 8px; width: 100%"
            @click="doSearch"
          >查询</a-button>
        </div>

        <!-- 用户列表（纵向） -->
        <div class="uq-user-list">
          <div
            v-for="user in users"
            :key="user.userId"
            class="uq-user-chip"
            :class="{ active: selectedUserId === user.userId }"
            @click="selectUser(user)"
          >
            <div class="uq-chip-name">{{ user.nickName }}</div>
            <div class="uq-chip-meta">ID: {{ user.userId }} · {{ user.appName }}</div>
            <div class="uq-chip-meta">
              {{ vipGradeText(user.vipGrade) }}
              <span v-if="user.vipGrade > 0"> · {{ rankText(user.rank) }} · 到期: {{ formatTimestamp(user.vipExpireDate) }}</span>
            </div>
            <div class="uq-chip-meta">渠道: {{ user.channel }} · 注册: {{ user.createTime }}</div>
          </div>
        </div>
      </div>

      <!-- ========== 右侧内容区域 ========== -->
      <div class="uq-right">
        <div
          v-if="!selectedUserId"
          class="uq-empty-state"
        >
          <div class="uq-empty-icon">🔍</div>
          <div>通过手机号、订单号或支付时间查询用户</div>
        </div>

        <div
          v-else
          class="uq-content"
        >
          <!-- 订单区域 -->
          <OrderSection
            :orders-loading="ordersLoading"
            :agreements="agreements"
            :all-orders-loading="allOrdersLoading"
            :all-orders="allOrders"
            :refund-orders-loading="refundOrdersLoading"
            :refund-orders="refundOrders"
            @unsign="handleUnsign"
            @refund="handleRefund"
            @toast="showToast"
          />

          <!-- 算力区域 -->
          <CreditSection
            ref="creditSection"
            :credit-account-loading="creditAccountLoading"
            :credit-account="creditAccount"
            :credit-loading="creditLoading"
            :credit-records="creditRecords"
            :credit-page="creditPage"
            :credit-page-count="creditPageCount"
            :returning-task-no="returningTaskNo"
            :granting="granting"
            @return-credit="handleReturnCredit"
            @page-change="handleCreditPage"
            @grant-credit="handleGrantCredit"
          />

          <!-- AI 任务区域 -->
          <AiTaskSections
            :tasks-loading="tasksLoading"
            :vidu-tasks="viduTasks"
            :vidu-page="viduPage"
            :vidu-page-count="viduPageCount"
            :lip-sync-tasks="lipSyncTasks"
            :lip-sync-page="lipSyncPage"
            :lip-sync-page-count="lipSyncPageCount"
            :draw-tasks="drawTasks"
            :draw-page="drawPage"
            :draw-page-count="drawPageCount"
            @preview="preview"
            @page="handleTaskPage"
          />
        </div>
      </div>
    </div>

    <!-- 图片预览浮层 -->
    <div
      v-if="previewUrl"
      class="uq-preview-overlay"
      @click="previewUrl = ''"
    >
      <img
        :src="previewUrl"
        class="uq-preview-img"
      />
    </div>

    <!-- Toast -->
    <transition name="uq-toast-fade">
      <div
        v-if="toastMsg"
        class="uq-toast"
      >{{ toastMsg }}</div>
    </transition>
  </div>
</template>

<script>
import { message } from "ant-design-vue";
import OrderSection from "@/components/userquery/OrderSection.vue";
import CreditSection from "@/components/userquery/CreditSection.vue";
import AiTaskSections from "@/components/userquery/AiTaskSections.vue";
import { vipGradeText, rankText, formatTimestamp } from "@/utils/formatters";
import {
  searchUsersByPhone,
  searchByOrderNo,
  searchByPayTime,
  getAgreementOrders,
  getPaidOrders,
  getRefundOrders,
  unsignAgreement,
  singleRefund,
  getViduTasks,
  getLipSyncTasks,
  getDrawTasks,
  getCreditAccount,
  getCreditRecords,
  returnCredit,
  grantCredit,
} from "@/utils/userQueryApi";

export default {
  name: "UserQueryView",
  components: { OrderSection, CreditSection, AiTaskSections },
  data() {
    return {
      searchMode: "phone",
      phone: "",
      orderNo: "",
      payTimeStart: null,
      payTimeEnd: null,
      loading: false,
      users: [],
      selectedUserId: null,

      ordersLoading: false,
      ordersRequestId: 0,
      agreements: [],
      allOrders: [],
      allOrdersLoading: false,
      refundOrders: [],
      refundOrdersLoading: false,
      refundOrdersRequestId: 0,

      creditAccountLoading: false,
      creditAccount: "-",
      creditLoading: false,
      creditRecords: [],
      creditPage: 1,
      creditPageCount: 1,
      returningTaskNo: "",
      granting: false,

      tasksLoading: false,
      viduTasks: [],
      viduPage: 1,
      viduPageCount: 1,
      lipSyncTasks: [],
      lipSyncPage: 1,
      lipSyncPageCount: 1,
      drawTasks: [],
      drawPage: 1,
      drawPageCount: 1,

      previewUrl: "",
      toastMsg: "",
      toastTimer: null,
    };
  },
  beforeUnmount() {
    this.ordersRequestId += 1;
    this.refundOrdersRequestId += 1;
  },
  methods: {
    vipGradeText,
    rankText,
    formatTimestamp,

    parseInput() {
      const lines = this.phone
        .split(/[\n\r,，]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const phoneList = [];
      for (const line of lines) {
        const phoneMatch = line.match(/(1\d{10})/);
        if (phoneMatch) phoneList.push(phoneMatch[1]);
      }
      return phoneList.join(",");
    },

    async doSearch() {
      if (this.searchMode === "phone") {
        await this.searchByPhone();
      } else if (this.searchMode === "order") {
        await this.searchByOrder();
      } else if (this.searchMode === "time") {
        await this.searchByTime();
      }
    },

    resetResults() {
      this.ordersRequestId += 1;
      this.ordersLoading = false;
      this.refundOrdersRequestId += 1;
      this.refundOrdersLoading = false;
      this.refundOrders = [];
      this.users = [];
      this.selectedUserId = null;
      this.agreements = [];
      this.creditAccount = "-";
      this.creditRecords = [];
      this.creditPage = 1;
      this.creditPageCount = 1;
      this.clearTasks();
    },

    handleSearchResult(res) {
      const appUsers =
        res.code === 200 && res.data && res.data.users ? res.data.users : [];
      this.users = appUsers;
      if (this.users.length === 1) {
        this.selectUser(this.users[0]);
      } else if (!this.users.length) {
        message.info("查询无数据");
      }
    },

    async searchByPhone() {
      if (!this.phone) return;
      const phones = this.parseInput();
      if (!phones) {
        message.warning("未识别到有效手机号");
        return;
      }
      this.loading = true;
      this.resetResults();
      try {
        const res = await searchUsersByPhone(phones);
        this.handleSearchResult(res);
      } catch (e) {
        message.error("请求失败: " + (e.message || "未知错误"));
      } finally {
        this.loading = false;
      }
    },

    async searchByOrder() {
      if (!this.orderNo) {
        message.warning("请输入订单编号");
        return;
      }
      this.loading = true;
      this.resetResults();
      try {
        const res = await searchByOrderNo(this.orderNo.trim());
        this.handleSearchResult(res);
      } catch (e) {
        message.error("请求失败: " + (e.message || "未知错误"));
      } finally {
        this.loading = false;
      }
    },

    async searchByTime() {
      if (!this.payTimeStart || !this.payTimeEnd) {
        message.warning("请选择开始和结束日期");
        return;
      }
      this.loading = true;
      this.resetResults();
      try {
        const res = await searchByPayTime(this.payTimeStart, this.payTimeEnd);
        this.handleSearchResult(res);
      } catch (e) {
        message.error("请求失败: " + (e.message || "未知错误"));
      } finally {
        this.loading = false;
      }
    },

    async selectUser(user) {
      this.selectedUserId = user.userId;
      this.creditPage = 1;
      await Promise.all([
        this.loadOrders(),
        this.loadAllOrders(),
        this.loadRefundOrders(),
        this.loadAllTasks(),
        this.loadCreditAccount(),
        this.loadCreditRecords(),
      ]);
    },

    async loadCreditAccount() {
      if (!this.selectedUserId) return;
      this.creditAccountLoading = true;
      this.creditAccount = "-";
      try {
        const res = await getCreditAccount(this.selectedUserId);
        if (res.code === 200 && res.data) {
          this.creditAccount = res.data.credit ?? "-";
        }
      } catch {
        /* ignore */
      } finally {
        this.creditAccountLoading = false;
      }
    },

    async loadCreditRecords() {
      if (!this.selectedUserId) return;
      this.creditLoading = true;
      this.creditRecords = [];
      try {
        const res = await getCreditRecords(this.selectedUserId, this.creditPage);
        if (res.code === 200 && res.data) {
          this.creditRecords = res.data.data || [];
          this.creditPageCount = res.data.pageCount || 1;
        }
      } catch {
        /* ignore */
      } finally {
        this.creditLoading = false;
      }
    },

    handleCreditPage(page) {
      this.creditPage = page;
      this.loadCreditRecords();
    },

    async handleReturnCredit(record) {
      if (!record || !record.taskNo) {
        this.showToast("当前记录缺少 taskNo，无法返还");
        return;
      }
      this.returningTaskNo = record.taskNo;
      try {
        const res = await returnCredit(this.selectedUserId, record.taskNo);
        if (res.code === 200) {
          this.showToast("算力返还成功");
          await Promise.all([this.loadCreditAccount(), this.loadCreditRecords()]);
        } else {
          this.showToast("算力返还失败: " + (res.msg || res.message || res.content || "未知错误"));
        }
      } catch (e) {
        this.showToast("算力返还请求异常: " + (e.message || "未知错误"));
      } finally {
        this.returningTaskNo = "";
      }
    },

    async handleGrantCredit(credit) {
      if (!this.selectedUserId) return;
      this.granting = true;
      try {
        const res = await grantCredit(this.selectedUserId, credit);
        if (res.code === 200) {
          this.showToast("算力赠送成功");
          if (this.$refs.creditSection) {
            this.$refs.creditSection.resetGrantInput();
          }
          await Promise.all([this.loadCreditAccount(), this.loadCreditRecords()]);
        } else {
          this.showToast("算力赠送失败: " + (res.msg || res.message || res.content || "未知错误"));
        }
      } catch (e) {
        this.showToast("算力赠送请求异常: " + (e.message || "未知错误"));
      } finally {
        this.granting = false;
      }
    },

    async loadOrders() {
      const requestId = ++this.ordersRequestId;
      const userId = this.selectedUserId;
      this.ordersLoading = true;
      this.agreements = [];
      try {
        const res = await getAgreementOrders(userId);
        if (requestId !== this.ordersRequestId || userId !== this.selectedUserId) return;
        if (res.code === 200) this.agreements = res.data || [];
      } catch {
        /* ignore */
      } finally {
        if (requestId === this.ordersRequestId) this.ordersLoading = false;
      }
    },

    async handleUnsign(externalAgreementNo, done) {
      try {
        const res = await unsignAgreement(externalAgreementNo);
        if (res.code === 200) {
          this.showToast("解约成功");
          await this.loadOrders();
        } else {
          this.showToast("解约失败: " + (res.msg || res.message || "未知错误"));
        }
      } catch {
        this.showToast("解约请求异常");
      } finally {
        if (done) done();
      }
    },

    async handleRefund(tradeNo, done) {
      try {
        const res = await singleRefund(tradeNo);
        if (res.code === 200 && res.data && res.data.code === 0) {
          this.showToast("退款成功");
          await Promise.all([
            this.loadOrders(),
            this.loadAllOrders(),
            this.loadRefundOrders(),
          ]);
        } else {
          this.showToast("退款失败: " + (res.msg || res.message || "未知错误"));
        }
      } catch (e) {
        this.showToast("退款请求失败: " + (e.message || "未知错误"));
      } finally {
        if (done) done();
      }
    },

    async loadAllOrders() {
      this.allOrdersLoading = true;
      try {
        const res = await getPaidOrders(this.selectedUserId);
        this.allOrders = res.code === 200 ? res.data || [] : [];
      } catch {
        this.allOrders = [];
      } finally {
        this.allOrdersLoading = false;
      }
    },

    async loadRefundOrders() {
      const requestId = ++this.refundOrdersRequestId;
      const userId = this.selectedUserId;
      this.refundOrdersLoading = true;
      this.refundOrders = [];
      try {
        const res = await getRefundOrders(userId);
        if (requestId !== this.refundOrdersRequestId || userId !== this.selectedUserId) return;
        this.refundOrders = res.code === 200 ? res.data || [] : [];
      } catch {
        if (requestId === this.refundOrdersRequestId && userId === this.selectedUserId) {
          this.refundOrders = [];
        }
      } finally {
        if (requestId === this.refundOrdersRequestId) this.refundOrdersLoading = false;
      }
    },

    clearTasks() {
      this.viduTasks = [];
      this.lipSyncTasks = [];
      this.drawTasks = [];
    },

    async loadAllTasks() {
      this.tasksLoading = true;
      this.clearTasks();
      await Promise.all([
        this.loadViduTasks(),
        this.loadLipSyncTasks(),
        this.loadDrawTasks(),
      ]);
      this.tasksLoading = false;
    },

    async loadViduTasks() {
      try {
        const res = await getViduTasks(this.selectedUserId, this.viduPage);
        if (res.code === 200 && res.data) {
          this.viduTasks = res.data.data || [];
          this.viduPageCount = res.data.pageCount || 1;
        }
      } catch {
        /* ignore */
      }
    },

    async loadLipSyncTasks() {
      try {
        const res = await getLipSyncTasks(
          this.selectedUserId,
          this.lipSyncPage
        );
        if (res.code === 200 && res.data) {
          this.lipSyncTasks = res.data.data || [];
          this.lipSyncPageCount = res.data.pageCount || 1;
        }
      } catch {
        /* ignore */
      }
    },

    async loadDrawTasks() {
      try {
        const res = await getDrawTasks(this.selectedUserId, this.drawPage);
        if (res.code === 200 && res.data) {
          this.drawTasks = res.data.data || [];
          this.drawPageCount = res.data.pageCount || 1;
        }
      } catch {
        /* ignore */
      }
    },

    handleTaskPage(type, page) {
      const map = {
        vidu: { pageKey: "viduPage", loader: "loadViduTasks" },
        lipSync: { pageKey: "lipSyncPage", loader: "loadLipSyncTasks" },
        draw: { pageKey: "drawPage", loader: "loadDrawTasks" },
      };
      const entry = map[type];
      if (entry) {
        this[entry.pageKey] = page;
        this[entry.loader]();
      }
    },

    preview(url) {
      this.previewUrl = url;
    },

    showToast(msg, duration = 2000) {
      clearTimeout(this.toastTimer);
      this.toastMsg = msg;
      this.toastTimer = setTimeout(() => {
        this.toastMsg = "";
      }, duration);
    },
  },
};
</script>

<style scoped>
.uq-page {
  padding: 0;
  height: calc(100vh - 64px - 70px - 48px);
  overflow: hidden;
}

/* ========== 左右布局 ========== */
.uq-layout {
  display: flex;
  height: 100%;
}

/* ========== 左侧面板 ========== */
.uq-left {
  width: 280px;
  min-width: 280px;
  background: #fff;
  border-right: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.uq-search-box {
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
}
.uq-user-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}
.uq-user-chip {
  padding: 10px 12px;
  background: #fafafa;
  border: 1px solid #e8e5e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  margin-bottom: 8px;
}
.uq-user-chip:hover {
  background: #f0efed;
}
.uq-user-chip.active {
  background: #e6f4ff;
  border-color: #1677ff;
}
.uq-chip-name {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 2px;
  color: #37352f;
}
.uq-chip-meta {
  font-size: 12px;
  color: #9b9a97;
  line-height: 1.6;
}

/* ========== 右侧内容区 ========== */
.uq-right {
  flex: 1;
  overflow-y: auto;
  background: #f5f5f5;
}

/* ========== 空状态 ========== */
.uq-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9b9a97;
  font-size: 15px;
  gap: 12px;
}
.uq-empty-icon {
  font-size: 40px;
}

/* ========== 内容区 ========== */
.uq-content {
  padding: 16px 24px;
}

/* ========== 图片预览 ========== */
.uq-preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  cursor: pointer;
}
.uq-preview-img {
  max-width: 90vw;
  max-height: 90vh;
  border-radius: 6px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

/* ========== Toast ========== */
.uq-toast {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 24px;
  background: #323232;
  color: #fff;
  font-size: 14px;
  border-radius: 6px;
  z-index: 1100;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.uq-toast-fade-enter-active,
.uq-toast-fade-leave-active {
  transition: opacity 0.25s;
}
.uq-toast-fade-enter-from,
.uq-toast-fade-leave-to {
  opacity: 0;
}
</style>
